/**
 * EntryForm — écriture de journal manuelle (lignes débit / crédit équilibrées).
 */
import { useState } from "react";
import { Plus, Save, Send, Trash2 } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { r2 } from "@/lib/format";
import { AccountSelect, Btn, Field, JOURNAL_LABEL, Modal, errMsg, inputCls, money, today, useGlBase } from "./_shared";

const blank = () => ({ account_id: "", description: "", debit: "", credit: "" });

export default function EntryForm({ entry, accounts, onClose, onSaved }) {
  const base = useGlBase();
  const isNew = !entry;
  const [f, setF] = useState(entry
    ? { date: entry.date, journal: entry.journal, memo: entry.memo || "", reference: entry.reference || "", lines: entry.lines.map((l) => ({ account_id: l.account_id, description: l.description || "", debit: l.debit || "", credit: l.credit || "" })) }
    : { date: today(), journal: "GEN", memo: "", reference: "", lines: [blank(), blank()] });
  const [busy, setBusy] = useState(false);
  const setLine = (i, k, v) => setF((p) => ({ ...p, lines: p.lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));
  const dr = r2(f.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0));
  const cr = r2(f.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0));
  const balanced = Math.abs(dr - cr) < 0.005 && dr > 0;

  const save = async (post = false) => {
    if (!f.date) return toast.error("Date requise");
    if (!balanced) return toast.error("L'écriture doit être équilibrée (débits = crédits)");
    setBusy(true);
    try {
      const payload = { ...f, post, lines: f.lines.filter((l) => l.account_id && (parseFloat(l.debit) || parseFloat(l.credit))).map((l) => ({ ...l, debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0 })) };
      if (isNew) await api.post(`${base}/entries`, payload); else await api.put(`${base}/entries/${entry.id}`, payload);
      toast.success(post ? "Écriture reportée au grand livre" : isNew ? "Écriture enregistrée (non reportée)" : "Écriture modifiée");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={isNew ? "Nouvelle écriture de journal" : "Écriture non reportée"} sub="Chaque ligne porte soit un débit, soit un crédit. Les débits doivent égaler les crédits. L'écriture reste « non reportée » tant qu'elle n'est pas reportée au grand livre." onClose={onClose} wide testId="entry-form"
      footer={<>
        <div className={`mr-auto text-xs tabular-nums ${balanced ? "text-emerald-700" : "text-amber-700"}`} data-testid="entry-form-totals">
          Débits {money(dr)} · Crédits {money(cr)} · {balanced ? "Équilibrée ✓" : `Écart ${money(Math.abs(dr - cr))}`}
        </div>
        <Btn tone="slate" onClick={onClose} testId="entry-form-cancel">Annuler</Btn>
        <Btn tone="slate" icon={Save} onClick={() => save(false)} busy={busy} disabled={!balanced} testId="entry-form-save">{isNew ? "Enregistrer" : "Modifier"}</Btn>
        <Btn icon={Send} onClick={() => save(true)} busy={busy} disabled={!balanced} testId="entry-form-save-post">Enregistrer et reporter au GL</Btn>
      </>}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Field label="Date *"><input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inputCls} data-testid="entry-form-date" /></Field>
        <Field label="Journal">
          <select value={f.journal} onChange={(e) => setF({ ...f, journal: e.target.value })} className={inputCls} data-testid="entry-form-journal">
            {Object.entries(JOURNAL_LABEL).map(([k, l]) => <option key={k} value={k}>{k} — {l}</option>)}
          </select>
        </Field>
        <Field label="Référence" className="col-span-2 sm:col-span-1"><input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className={inputCls} placeholder="N° pièce, chèque…" maxLength={80} data-testid="entry-form-reference" /></Field>
        <Field label="Description" className="col-span-2 sm:col-span-1"><input value={f.memo} onChange={(e) => setF({ ...f, memo: e.target.value })} className={inputCls} placeholder="Objet de l'écriture" maxLength={300} data-testid="entry-form-memo" /></Field>
      </div>

      <div className="border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr><th className="text-left px-2 py-2">Compte</th><th className="text-left px-2 py-2 hidden md:table-cell w-48">Libellé</th><th className="text-right px-2 py-2 w-32">Débit</th><th className="text-right px-2 py-2 w-32">Crédit</th><th className="w-8"></th></tr>
          </thead>
          <tbody className="divide-y">
            {f.lines.map((l, i) => (
              <tr key={i} data-testid={`entry-line-${i}`}>
                <td className="px-2 py-1.5"><AccountSelect accounts={accounts} value={l.account_id} onChange={(v) => setLine(i, "account_id", v)} testId={`entry-line-account-${i}`} /></td>
                <td className="px-2 py-1.5 hidden md:table-cell"><input value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} className={inputCls} maxLength={200} data-testid={`entry-line-desc-${i}`} /></td>
                <td className="px-2 py-1.5"><input type="number" step="0.01" min="0" value={l.debit} onChange={(e) => setLine(i, "debit", e.target.value)} onFocus={() => l.credit && setLine(i, "credit", "")} className={`${inputCls} text-right font-mono`} data-testid={`entry-line-debit-${i}`} /></td>
                <td className="px-2 py-1.5"><input type="number" step="0.01" min="0" value={l.credit} onChange={(e) => setLine(i, "credit", e.target.value)} onFocus={() => l.debit && setLine(i, "debit", "")} className={`${inputCls} text-right font-mono`} data-testid={`entry-line-credit-${i}`} /></td>
                <td className="px-1"><button onClick={() => setF((p) => ({ ...p, lines: p.lines.filter((_, j) => j !== i) }))} disabled={f.lines.length <= 2} className="text-slate-300 hover:text-rose-600 disabled:opacity-30 p-1" data-testid={`entry-line-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="p-2 bg-slate-50 border-t">
          <Btn tone="ghost" icon={Plus} onClick={() => setF((p) => ({ ...p, lines: [...p.lines, { ...blank(), ...(dr > cr ? { credit: r2(dr - cr) } : cr > dr ? { debit: r2(cr - dr) } : {}) }] }))} testId="entry-line-add">Ajouter une ligne</Btn>
        </div>
      </div>
    </Modal>
  );
}
